import { useCallback } from 'react';

import { useUser } from 'modules/user/hooks/useUser';

import { useAccount } from './useAccount';
import { useAccountSetters } from './useAccountSetters';

export interface UseSelectAccountProps {
  accountAddress: string;
  onSelect?: () => void;
}

export const useSelectAccount = ({
  accountAddress,
  onSelect,
}: UseSelectAccountProps) => {
  const { address: userAddress } = useUser();
  const {
    group: selectedGroupAccountAddress,
    isAccountPremissionDialogHidden,
    isPersonalAccount: isPersonalAccountSelected,
  } = useAccount();

  const isPersonalAccount = accountAddress === userAddress;

  const isSelected = isPersonalAccount
    ? isPersonalAccountSelected
    : accountAddress === selectedGroupAccountAddress;

  const teamAccountAddress = isPersonalAccount ? undefined : accountAddress;

  const {
    setIsAccountPremissionDialogHidden,
    setIsWorkerAuthorized,
    setTeamAccountAddress,
    setUserEndpointToken,
    setWorkerToken,
  } = useAccountSetters();

  const selectAccount = useCallback(
    async (newAccountAddress?: string) => {
      if (!isSelected || newAccountAddress) {
        setIsAccountPremissionDialogHidden(true);

        setUserEndpointToken(undefined);
        setWorkerToken(undefined);
        setIsWorkerAuthorized(false);

        setTeamAccountAddress(newAccountAddress ?? teamAccountAddress);

        onSelect?.();

        setIsAccountPremissionDialogHidden(isAccountPremissionDialogHidden);
      }
    },
    // isAccountPremissionDialogHidden constant should not be tracked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      teamAccountAddress,
      isSelected,
      onSelect,
      setIsAccountPremissionDialogHidden,
      setIsWorkerAuthorized,
      setTeamAccountAddress,
      setUserEndpointToken,
      setWorkerToken,
    ],
  );

  return { selectAccount, isSelected };
};
